import React, { ReactElement, ReactNode, useState } from 'react';
import classnames from 'classnames/bind';
import { ExpandToggle, Typography } from '@weareopenr/component-library';
import styles from './CollapsibleSection.module.scss';

const cx = classnames.bind(styles);

interface ICollapsibleSection {
  title: ReactNode;
  number?: string;
  subtitle?: string | ReactNode;
  children?: ReactElement;
  size?: 'small' | 'medium' | 'large';
  defaultExpanded?: boolean;
  bordered?: boolean;
  arrowPosition?: 'left' | 'right';
  arrowColour?: 'purple' | 'liquorice';
  scrollable?: boolean;
  compact?: boolean;
}

const CollapsibleSection = ({
  number,
  title,
  subtitle,
  children,
  size = 'large',
  defaultExpanded = false,
  bordered = false,
  arrowPosition = 'right',
  arrowColour = 'purple',
  scrollable = true,
  compact = false,
}: ICollapsibleSection) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isScrollable, setIsScrollable] = useState(scrollable && defaultExpanded);

  const handleToggle = () => {
    if (isExpanded) setIsScrollable(false);
    setIsExpanded((prev) => !prev);
  };

  const isCollapsible = !!children;

  return (
    <div className={cx('collapsible-section', { bordered })}>
      <div
        className={cx('header-container', {
          'header-container-clickable': isCollapsible,
          'header-container-compact': compact,
        })}
        {...(isCollapsible && {
          role: 'button',
          tabIndex: 0,
          onClick: handleToggle,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggle();
            }
          },
        })}
      >
        <div className={styles['header']}>
          {number && (
            <div className={styles['number']}>
              <Typography colour="secondary" weight="bold">
                {number}
              </Typography>
            </div>
          )}
          <Typography
            size={size === 'large' ? 'xlarge' : 'medium'}
            weight={size === 'medium' ? 'bold' : 'semi-bold'}
            className={cx('title', { 'title-medium': size === 'medium', 'title-compact': compact })}
          >
            {title}
          </Typography>
          {isCollapsible && (
            <ExpandToggle
              isExpanded={isExpanded}
              size="small"
              aria-hidden
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
              className={cx(
                'expand-toggle',
                `expand-toggle-${arrowColour}`,
                `expand-toggle-${size}`,
                `expand-toggle-${arrowPosition}`,
              )}
            />
          )}
        </div>
        {subtitle && (
          <div className={cx(`subtitle-${size}`, { 'margin-left': number })}>
            {typeof subtitle === 'string' ? <Typography>{subtitle}</Typography> : subtitle}
          </div>
        )}
      </div>
      {children && (
        <div
          className={cx('collapsible-body', {
            'margin-left': number,
            'collapsible-body-expanded': isExpanded,
            'is-scrollable': isScrollable,
            scrollable,
          })}
          onTransitionEnd={(e) => {
            if (e.propertyName === 'max-height' && isExpanded && scrollable) {
              setIsScrollable(true);
            }
          }}
        >
          <div>{children}</div>
        </div>
      )}
    </div>
  );
};

export default CollapsibleSection;
